export type OrderByDirection = "asc" | "desc";
export type ModelType = "profile" | "event";
export type Operator =
  | "eq"
  | "fuzzy"
  | "soundex"
  | "levenstein_90"
  | "levenstein_70"
  | "jaro_winkler_90"
  | "jaro_winkler_70";
export type Transformation =
  | "case-insensitive"
  | "normalize-whitespace"
  | "extract-number";
export type BucketingScheme = "first-3-chars" | "first-n-chars";

// List of merge rule types that are considered "fuzzy". We use this to separate
// rules, since we evaluate fuzzy ones differently.
export const FUZZY_RULE_OPERATORS = [
  "levenstein_90",
  "levenstein_70",
  "jaro_winkler_90",
  "jaro_winkler_70",
];

export interface BucketingConfig {
  scheme: BucketingScheme;
  config?: {
    numChars: number;
  };
}

export interface MergeRule {
  identifier: string;
  operator: Operator;
  transformations?: Transformation[];
  bucketing?: BucketingConfig;
}

export interface MergeRuleCondition {
  type: "and" | "or";
  rules: MergeRule[];
}

export interface MergeRules {
  type: "and" | "or";
  conditions: MergeRuleCondition[];
}

export interface BlockRule {
  identifier: string;
  limit: number;
}

export interface CheckpointContext {
  // Step name, or IDR_RUN_START / IDR_RUN_COMPLETE.
  checkpoint: string;
  maxHtIdInEdges?: number;
  subcheckpoint?: number;
  iteration?: number;
  // `configVersion` and `materializedSteps` are not part of the checkpoint identity.
  //  They are used to verify that the checkpoint configuration hasn't changed between runs.
  configVersion?: number;
  // Rewind attempts in this run tracked across all steps.
  rewindAttempts?: number;
  materializedSteps?:
    | {
        name: string;
        version?: number;
        // Absent for a step that runs once: JSON drops the undefined on persist.
        numIterations?: number;
        // Subcheckpoint names
        subcheckpoints: string[];
      }[]
    | undefined;
}

// Not-started sentinel. Reserved — cannot be used as an IDR step name.
export const IDR_RUN_START = "__idr_run_start__";

// Completed-run sentinel. Reserved — cannot be used as an IDR step name.
export const IDR_RUN_COMPLETE = "__idr_run_complete__";

export const RESERVED_CHECKPOINT_NAMES: ReadonlySet<string> = new Set([
  IDR_RUN_START,
  IDR_RUN_COMPLETE,
]);

// A checkpoint manager's position within a run: step name (or IDR_RUN_START /
// IDR_RUN_COMPLETE), plus subcheckpoint and iteration indices within that step.
export type CheckpointCursor = {
  checkpoint: string;
  subcheckpoint: number;
  iteration: number;
};

export function makeCursor(
  checkpoint: string,
  subcheckpoint = 0,
  iteration = 0,
): CheckpointCursor {
  return { checkpoint, subcheckpoint, iteration };
}

export type ResumeDecision = {
  kind: "resume" | "restart";
  cursor: CheckpointCursor;
};

// Result of a single attempt of a step. On a resumed run, `durationMs` times
// only this attempt and `subcheckpointsRun` counts only the subcheckpoints this
// attempt executed — not ones a prior attempt already completed.
export type StepRunOutcome = {
  durationMs: number;
  subcheckpointsRun: number;
  totalSubcheckpoints: number;
};

export const DEFAULT_MAX_REWIND_ATTEMPTS = 3;

export type RewindRequest = {
  // Name of an enabled step earlier in the plan.
  rewindTo: string;
  // Called when the run's rewind budget is spent. Return an error to fail the
  // run, or undefined to run the step's remaining subcheckpoints and continue.
  onExhausted: () => Promise<Error | undefined>;
};

export type IdrSubcheckpoint = {
  // Unique identity of the subcheckpoint. Renaming is treated as a plan mismatch.
  name: string;
  // The function to execute for each subCheckpoint. If numIterations is
  // specified, the iteration number will be passed in to each invocation of
  // this function. If isResumingStep is true, the step was interrupted on a
  // prior attempt. Useful if the code needs/wants to do any cleanup from a
  // partial execution of this step. Returning a RewindRequest re-enters an
  // earlier step instead of continuing.
  fn: (
    iteration?: number | undefined,
    isResumingStep?: boolean,
  ) => Promise<void | RewindRequest>;
  // Observability label only; defaults to `name`. Not part of identity.
  trace?: string;
};

export type IdrStep = {
  // Unique identity of the step. Renaming is treated as a plan mismatch.
  name: string;
  // Version number for this step. Defaults to 0.
  // Bump when the implementation changes in a non-backwards-compatible way.
  version?: number;
  trace?: string;
  subcheckpoints: IdrSubcheckpoint[];
  numIterations?: number;
  disabled?: boolean;
  // Fires on every non-error exit from a step.
  onComplete?: (outcome: StepRunOutcome) => Promise<void> | void;
  onError?: (outcome: StepRunOutcome, error: unknown) => Promise<void> | void;
};

export type IdrRunConfiguration = {
  configVersion: number;
  // Total rewinds allowed for the run tracked across all steps.
  maxRewindAttempts?: number;
  steps: IdrStep[];
};

// Snapshot of a single step's identity, as persisted for exact-match resume
// checks. Same shape as CheckpointContext.materializedSteps entries.
export type MaterializedStep = NonNullable<
  CheckpointContext["materializedSteps"]
>[number];

// Snapshot of the enabled step sequence for exact-match resume checks.
// Disabled steps are omitted so toggling `disabled` reads as an insert or a
// removal rather than passing unnoticed.
export function materializeIdrSteps(steps: IdrStep[]): MaterializedStep[] {
  return steps
    .filter((step) => !step.disabled)
    .map((step) => ({
      name: step.name,
      version: step.version ?? 0,
      numIterations: step.numIterations,
      subcheckpoints: step.subcheckpoints.map(
        (subcheckpoint) => subcheckpoint.name,
      ),
    }));
}

// Finds the index of a step by name, or undefined if the name is absent.
export function findStepIndex(
  steps: readonly { name: string }[],
  stepName: string,
): number | undefined {
  const index = steps.findIndex((step) => step.name === stepName);
  return index === -1 ? undefined : index;
}

// Finds index of step by name or else throws an error.
export function requireStepIndex(
  steps: readonly { name: string }[],
  stepName: string,
): number {
  const index = findStepIndex(steps, stepName);

  if (index === undefined) {
    throw new Error(`Unknown step name "${stepName}"`);
  }
  return index;
}

// snake_case for stats keys because we return the stats transparently to the GQL client.
export interface Stats {
  profiles: StatsForModelType;
  events: StatsForModelType;
  num_ht_ids: number;
}

// Statistics for a given model type, e.g. events or profiles
export interface StatsForModelType {
  new: number;
  num_ht_ids: number;
  source_rows: number;
  models: ModelStats[];
}

// Statistics for a specific model
export interface ModelStats {
  id: string;
  source_rows: number;
  num_ht_ids: number;
}

export const IDRv1OutputTableSuffix = "_resolved_identities";
